<script setup lang="ts">
import { ref, onMounted, computed, watch, onUnmounted } from "vue";
import type { Workspace, User } from "~/types";
import {
    IconCircleCheckFilled,
    IconLogout,
    IconChevronDown,
} from "@tabler/icons-vue";
import { useForm } from "vee-validate";
const { $api, $yup } = useNuxtApp();
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/vue";
import Toggle from '@/components/form/toggle.vue';
import ToggleSidebar from '@/components/layout/toggle-sidebar.vue';
const router = useRouter();
const authStore = useAuthStore();
const userStore = useUserStore();
const workspaceStore = useWorkspaceStore();

const user = computed(() => userStore.user);
const workspaces = computed(() => workspaceStore.workspaces);

const logout = () => {
    $api.post(`/logout`).then(() => {

        // clear the auth
        authStore.logout();

        router.push('/auth/login');
    });
};

const switchToWorkspace = (workspace: Workspace) => {
    $api.post(`/workspaces/update-current`, {
        workspace_id: workspace.id,
    }).then(() => {
        window.location.reload();
    });
};


const schema = $yup.object({
    is_away: $yup.boolean().required(),
});

const { handleSubmit, meta, values, errors, setValues } = useForm({
    initialValues: {
        is_away: user.value?.is_away ?? false,
    },
    validationSchema: schema,
});

const submit = handleSubmit(async (form) => {
    $api.post(`/me/away`, {
        is_away: form.is_away,
    }).then((response) => {
        userStore.setUser({
            ...user.value,
            is_away: form.is_away,
        } as User);
    }).catch((error) => {
        console.error(error);
    });
});

watch(values, () => {
    submit();
}, { deep: true });

const updateAvailability = () => {
    $api.get('/me/availability')
}

const availabilityInterval = ref<NodeJS.Timeout | undefined>(undefined)
const startAvailabilityUpdate = () => {
    availabilityInterval.value = setInterval(() => {
        updateAvailability()
    }, 180000) // 3 min
}

onMounted(() => {
    startAvailabilityUpdate()
})

onUnmounted(() => {
    if (availabilityInterval.value) {
        clearInterval(availabilityInterval.value)
    }
})

</script>
<template>
    <div class="relative flex items-center justify-between space-x-2 w-full">
        <Menu as="div" >
            <MenuButton class="flex items-center justify-between space-x-2 cursor-pointer w-full">
                <div class="flex items-center space-x-2">
                    <span class="relative inline-block">
                        <img class="size-6 rounded-full" :src="user?.photo_url" :alt="user?.name" />
                        <span :class="{
                            'absolute bottom-0 -right-0.5 block size-2 rounded-full ring-2 ring-zinc-100 dark:ring-zinc-900': true,
                            'bg-green-500': !user?.is_away,
                            'bg-red-500': user?.is_away,
                        }" />
                    </span>
                    <span class="text-sm font-normal text-zinc-800 dark:text-zinc-200">
                        {{ user?.name }}
                    </span>
                </div>
                <IconChevronDown class="size-4 text-zinc-800 dark:text-zinc-300" />
            </MenuButton>

            <transition enter-active-class="transition ease-out duration-100"
                enter-from-class="transform opacity-0 scale-95" enter-to-class="transform opacity-100 scale-100"
                leave-active-class="transition ease-in duration-75" leave-from-class="transform opacity-100 scale-100"
                leave-to-class="transform opacity-0 scale-95">
                <MenuItems
                    class="fixed top-12 z-40 w-60 origin-top-right divide-y divide-zinc-100 dark:divide-zinc-700 rounded-lg bg-white dark:bg-zinc-800 shadow-2xl focus:outline-none border border-zinc-200 dark:border-zinc-700">
                    <div class="py-1">
                        <MenuItem v-slot="{ active }">
                        <NuxtLink to="/settings/personal/account" :class="[
                            active
                                ? 'bg-zinc-100 dark:bg-zinc-900 text-zinc-900 dark:text-zinc-300'
                                : 'text-zinc-500 dark:text-zinc-400',
                            ' px-4 py-1.5 text-sm w-full text-left flex items-center space-x-2 font-medium',
                        ]">
                            <span class="relative inline-block">
                                <NuxtImg :src="user?.photo_url" :alt="user?.name"
                                    format="webp"
                                    loading="lazy"
                                    :class="{
                                        'size-6 rounded-full': true,
                                    }" />
                                <span :class="{
                                    'absolute bottom-0 -right-0.5 block size-2 rounded-full ring-2 ring-zinc-100 dark:ring-zinc-900': true,
                                    'bg-green-500': !user?.is_away,
                                    'bg-red-500': user?.is_away,
                                }" />
                            </span>
                            <div>{{ user?.name }}</div>
                        </NuxtLink>
                        </MenuItem>
                        <div class="px-4 pt-2 pb-1.5">
                            <Toggle id="is_away" name="is_away" label="Away mode" orientation="right"
                                v-model:checked="values.is_away" />
                        </div>
                    </div>
                    <div class="py-1 max-h-52 overflow-y-auto">
                        <MenuItem v-for="workspace in workspaces" :key="workspace.id" v-slot="{ active }">
                        <div @click="switchToWorkspace(workspace)" :class="[
                            active
                                ? 'bg-zinc-100 dark:bg-zinc-900 text-zinc-900 dark:text-zinc-300'
                                : 'text-zinc-500 dark:text-zinc-400',
                            'cursor-pointer px-4 py-1.5 text-sm w-full text-left flex items-center space-x-2',
                        ]">
                            <div class="flex flex-1 items-start min-w-0">
                                <div class="flex items-center flex-1 space-x-2 min-w-0 mr-4">
                                    <div class="flex-none">
                                        <NuxtImg
                                            :src="workspace.logo_url"
                                            :alt="workspace.name"
                                            format="webp"
                                            loading="lazy"
                                            :class="{
                                                'size-6 rounded-lg': true,
                                            }"
                                        />
                                    </div>

                                    <div class="min-w-0">
                                        <div class="truncate font-normal text-zinc-800 dark:text-zinc-200 text-sm">
                                            {{ workspace.name }}
                                        </div>
                                    </div>
                                </div>
                                <div class="ml-auto" v-if="
                                    workspace.id ==
                                    user?.current_workspace_id
                                ">
                                    <IconCircleCheckFilled class="size-4 text-green-500" />
                                </div>
                            </div>
                        </div>
                        </MenuItem>
                    </div>
                    <div class="py-1">
                        <MenuItem v-slot="{ active }">
                        <NuxtLink to="/workspaces/create" :class="[
                            active
                                ? 'bg-zinc-100 dark:bg-zinc-900 text-zinc-900 dark:text-zinc-300'
                                : 'text-zinc-500 dark:text-zinc-400',
                            ' px-4 py-1.5 text-sm w-full text-left flex items-center space-x-2 font-normal',
                        ]">
                            <div>New Workspace</div>
                        </NuxtLink>
                        </MenuItem>
                    </div>

                    <div class="py-1">
                        <MenuItem v-slot="{ active }">
                        <button @click="logout" :class="[
                            active
                                ? 'bg-zinc-100 dark:bg-zinc-900 text-zinc-900 dark:text-zinc-300'
                                : 'text-zinc-500 dark:text-zinc-400',
                            ' px-4 py-1.5 text-sm w-full text-left flex items-center space-x-2',
                        ]">
                            <IconLogout class="w-4 h-4 stroke-2" />
                            <div>Sign Out</div>
                        </button>
                        </MenuItem>
                    </div>
                </MenuItems>
            </transition>
        </Menu>
        <ToggleSidebar />
    </div>
</template>